<template>
  <div class="card flex min-h-0 flex-1 flex-col overflow-hidden p-0">
    <template v-for="(group, index) in groups" :key="group.key">
      <div class="bg-gray-50 px-5 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500" :class="index === 0 ? 'border-b border-gray-200' : 'border-y border-gray-200'">
        {{ group.label }}
      </div>
      <ActivityRow v-for="event in group.events" :key="event.id" :event="event" :color="colorFor(event)" @select-po="emit('selectPo', $event)" />
    </template>
    <p v-if="events.length === 0" class="m-0 py-10 text-center text-sm text-gray-500">No changes match these filters.</p>
    <button v-if="hasMore" type="button" class="mt-auto border-t border-gray-100 px-5 py-2.5 text-center text-[13px] font-medium text-blue-700 hover:bg-gray-50" :disabled="loadingMore" @click="emit('loadMore')">
      {{ loadingMore ? 'Loading…' : 'Load older changes' }}
    </button>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import ActivityRow from '@/components/activity/ActivityRow.vue';
  import { useCalendarPrivacyStore } from '@/store/calendarPrivacyStore';
  import { useCustomerMappingStore } from '@/store/customerMappingStore';
  import { ASSIGNMENT_TYPE } from '@/types/capacityCalendar';
  import type { PoActivityEvent } from '@/types/poActivity';
  import { PO_ACTIVITY_SOURCE } from '@/types/poActivity';
  import { resolvePoSegmentColor, UNMAPPED_PO_COLOR } from '@/utils/customerColors';
  import { groupByDay } from '@/utils/poActivity';

  const props = withDefaults(defineProps<{ events: PoActivityEvent[]; hasMore: boolean; loadingMore: boolean; today?: Date }>(), { today: () => new Date() });
  const emit = defineEmits<{ loadMore: []; selectPo: [poNumber: string] }>();

  // Tracker events have no block to take a customer colour from; a neutral blue tells them from the board's rails.
  const TRACKER_COLOR = '#93C5FD';

  const mappingStore = useCustomerMappingStore();
  const privacyStore = useCalendarPrivacyStore();

  const groups = computed(() => groupByDay(props.events, props.today));

  // Same rail colour the board and calendar give this PO, so the feed reads as the same data.
  // Reservations take the unmapped colour, as they do on the board.
  function colorFor(event: PoActivityEvent): string {
    if (event.source === PO_ACTIVITY_SOURCE.TRACKER) return TRACKER_COLOR;
    if (event.assignmentType !== ASSIGNMENT_TYPE.po || !event.poNumber) return UNMAPPED_PO_COLOR;
    return resolvePoSegmentColor(event.poNumber, mappingStore.items, {
      hideCustomerMappings: privacyStore.hideCustomerMappings,
      index: 0,
    });
  }
</script>
